<script lang="ts">
  import {thumbnail, download, user, downloadDialog} from '@/store';
  import {canAccess, download as downloadText} from '../../utils/common';

  import Dialog from '@/components/dialog/Dialog.svelte';
  import Swal from 'sweetalert2';
  import {DraftService} from '@/services';
  import {downloadDiscussionGuide} from '../choose-video-redesign/Dialogs/VideoInformationDialog/DiscussionGuide';
  import {downloadTranscription} from '../choose-video-redesign/Dialogs/VideoInformationDialog/Transcription';
  import {Features} from '@/types/common';
  import {draftName} from '@/store/drafts';

  export let isDownloadDialogOpen = false;
  export let progress = 0;

  let title = '';
  $: title = $draftName || $thumbnail?.video?.title || 'Untitled Project';

  let downloadStarted = false;
  let downloadProgress = 0;
  let completeRequest = '';
  let downloadUrl = '';
  let showCloseButton = false;

  $: downloadUrl = $download.downloadableUrl || '';
  $: completeRequest = $download.completedRequestId ? $download.downloadableUrl || '#' : '#';

  function downloadClip(downloadString: string) {
    downloadProgress = 0;
    if (!downloadString) {
      return console.error('Unable to download null video string');
    }

    const filename = `${title}-SermonShots-${Date.now()}.mp4`;
    const xhr = new XMLHttpRequest();
    xhr.open('GET', completeRequest, true);
    xhr.responseType = 'blob';
    downloadStarted = true;

    xhr.onprogress = event => {
      if (!event.lengthComputable) return;
      const percentComplete = parseInt(((event.loaded / event.total) * 100).toFixed());
      downloadProgress = isNaN(percentComplete) ? 0 : percentComplete;
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        const blob = xhr.response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        a.remove();
      } else {
        console.error('Request failed with status:', xhr.status);
      }

      downloadProgress = 0;
      downloadStarted = false;

      Swal.fire({
        position: 'bottom-end',
        icon: 'success',
        title: 'Video Downloaded',
        showConfirmButton: false,
        timer: 10000,
        toast: true
      });
    };

    xhr.onerror = err => {
      downloadProgress = 0;
      downloadStarted = false;
      showCloseButton = true;
      if (err) console.error('Problem downloading tracking video using javascript', err);
      window.open(completeRequest as string);
    };

    xhr.send();
  }

  async function discussionGuide() {
    try {
      const result = await downloadDiscussionGuide($thumbnail?.video?.id || -1);
      downloadText(`${title}-discussion_guide.txt`, result.raw);
    } catch (error) {
      console.error(error);
      Swal.fire({
        position: 'bottom-end',
        icon: 'error',
        title: 'Discussion Guide Failed to Download',
        showConfirmButton: false,
        timer: 10000,
        toast: true
      });
    }
  }

  async function transcription() {
    try {
      const result = await downloadTranscription($thumbnail?.video?.id || -1);
      downloadText(`${title}-transcription.txt`, result);
    } catch (error) {
      console.error(error);
      Swal.fire({
        position: 'bottom-end',
        icon: 'error',
        title: 'Discussion Guide Failed to Download',
        showConfirmButton: false,
        timer: 10000,
        toast: true
      });
    }
  }

  function closeDialog() {
    downloadDialog.set(false);
  }
</script>

<Dialog isDialogOpen={isDownloadDialogOpen} useIfShow={true}>
  <div class="pointer-events-none flex h-full w-full items-center justify-center">
    <div
      class="pointer-events-auto relative mx-4 h-auto w-full min-w-[280px] max-w-full overflow-hidden rounded-xl border border-zinc-400 bg-white p-6 shadow-lg dark:border-zinc-600 dark:bg-zinc-700 lg:max-w-[400px]"
      style="z-index:99;"
    >
      <div class="flex w-full gap-2 pb-4">
        <span class="text-2xl font-bold">Rendering</span>
        <button on:click={closeDialog} class="ml-auto rounded-full px-2 hover:bg-zinc-100">
          <i class="fa-solid fa-xmark p-2 text-lg text-black" aria-hidden="true"></i>
        </button>
      </div>
      <div class="flex w-full items-center gap-4 pb-4">
        <p class="truncate">{title}</p>
        {#if progress == 0}
          <p class="text-xs text-zinc-500">You are in queue</p>
          <i class="fa fa-spinner animate-spin"></i>
        {:else}
          <div class="ml-auto rounded-full bg-zinc-100 px-2 py-1 text-sm font-medium text-zinc-900 hover:bg-zinc-100">
            {`${downloadUrl !== '' ? 'Complete' : `${progress}%`}`}
          </div>
        {/if}
      </div>
      <div class="w-full pb-8">
        {#if downloadUrl !== ''}
          <button
            on:click={() => downloadClip(completeRequest)}
            class="relative w-full overflow-hidden rounded-lg bg-gradient-to-r from-primary to-secondary text-center font-medium text-zinc-100"
          >
            {#if downloadStarted}
              <div class="py-2 opacity-0">hidden text</div>
              <div
                class="absolute left-0 top-0 h-full bg-rose-600 py-2 text-transparent"
                style="width: {downloadProgress}%;"
              ></div>
              <div class="absolute left-0 top-0 w-full py-2 text-center">{downloadProgress}%</div>
            {:else}
              <div class="w-full py-2">Download Sermon Clip</div>
            {/if}
          </button>
        {:else}
          <div class="h-3 w-full overflow-hidden rounded-full bg-zinc-300">
            <div
              class="z-20 h-3 animate-pulse rounded-full bg-gradient-to-r from-primary to-secondary transition-all duration-500 ease-in-out"
              style="width: {progress}%"
            ></div>
          </div>
        {/if}
      </div>
      {#if canAccess($user, Features.DISCUSSION_GUIDE) || canAccess($user, Features.TRANSCRIPTION)}
        <hr />
        <div class="relative w-full pt-8">
          <span class="font-bold">More downloadables for you</span>
          <div style="height: 156px; width: 100%;"></div>
          <div class="absolute top-0 z-10 grid w-full gap-6 py-4" style="padding-top: 5rem;">
            {#if canAccess($user, Features.DISCUSSION_GUIDE)}
              <div class="flex w-full items-center gap-2">
                <h1 class="text-sm font-bold">Discussion Guide</h1>
                <button
                  on:click={discussionGuide}
                  class="ml-auto rounded-lg bg-zinc-200 px-3 py-2 text-sm font-medium text-zinc-900 hover:bg-zinc-300"
                  >Download</button
                >
              </div>
            {/if}
            {#if canAccess($user, Features.TRANSCRIPTION)}
              <div class="flex w-full items-center gap-2">
                <h1 class="text-sm font-bold">Transcription</h1>
                <button
                  on:click={transcription}
                  class="ml-auto rounded-lg bg-zinc-200 px-3 py-2 text-sm font-medium text-zinc-900 hover:bg-zinc-300"
                  >Download</button
                >
              </div>
            {/if}
          </div>
        </div>
        <div style="width: 100%; position:absolute; bottom: 0; padding-left: 0.5rem;">
          <img
            class="width: 100%"
            src="https://storage.googleapis.com/sermonshots-homepage/v2/images/Group%201.svg"
            alt="bubbles"
          />
        </div>
      {/if}
    </div>
  </div>
</Dialog>
